import { WalletLoad } from "@apis/index";
import Button from "@components/button";
import Row from "@components/row";
import Table from "@components/table";
import Title from "@components/title";
import { useModal } from "@context/ModalContext";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import styled from "@emotion/styled";
import {
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  Wallet,
  User as UserIcon,
} from "lucide-react";
import useConfig from "@hooks/useConfig";
import Helper from "@utils/helper";
import { useGeneralSettings } from "@hooks/useGeneralSettings";

const WalletLoadRequests = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    status: "pending",
    search: "",
    startDate: "",
    endDate: "",
    dateRange: "",
  });
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { showModal, hideModal } = useModal();
  const { bucketUrl } = useConfig();
  const { settings } = useGeneralSettings();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res: any = await WalletLoad.list({ params });
      setData(res.items || []);
      setTotalItems(res.totalItems || 0);
      setTotalPages(res.totalPages || 0);
    } catch (error: any) {
      toast.error(error.message || "Failed to fetch wallet load requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [params]);

  const handleFilterChange = (key: string, value: string) => {
    let updates: any = { [key]: value, page: 1 };

    if (key === "dateRange") {
      const today = new Date();
      if (value === "today") {
        updates.startDate = today.toISOString().split("T")[0];
        updates.endDate = updates.startDate;
      } else if (value === "this_week") {
        const firstDay = new Date(
          today.setDate(today.getDate() - today.getDay()),
        );
        updates.startDate = firstDay.toISOString().split("T")[0];
        updates.endDate = new Date().toISOString().split("T")[0];
      } else if (value === "this_month") {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        updates.startDate = firstDay.toISOString().split("T")[0];
        updates.endDate = new Date().toISOString().split("T")[0];
      } else if (value === "custom") {
        // preserve current or let user pick
      } else {
        updates.startDate = "";
        updates.endDate = "";
      }
    }

    setParams((prev) => ({ ...prev, ...updates }));
  };

  const handleApprove = (item: any) => {
    showModal(
      <ActionModal
        type="approve"
        item={item}
        onClose={hideModal}
        onSuccess={() => {
          fetchData();
          hideModal();
        }}
      />,
    );
  };

  const handleReject = (item: any) => {
    showModal(
      <ActionModal
        type="reject"
        item={item}
        onClose={hideModal}
        onSuccess={() => {
          fetchData();
          hideModal();
        }}
      />,
    );
  };

  const columns = [
    {
      title: "ID",
      key: "id",
      render: (item: any) => <strong>#{item.id}</strong>,
    },
    {
      title: "User",
      key: "user",
      render: (item: any) => {
        const displayName =
          item.user?.role === "store" && item.store?.storeName
            ? item.store.storeName
            : item.user?.name || "Unknown";

        return (
          <UserInfo>
            <div className="icon">
              {item.user?.avatar ? (
                <img
                  src={`${bucketUrl}/${item.user.avatar}`}
                  alt={displayName}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                    const parent = target.parentElement;
                    if (parent) {
                      const div = document.createElement("div");
                      div.innerHTML =
                        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-user"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';
                      div.style.display = "flex";
                      div.style.alignItems = "center";
                      div.style.justifyContent = "center";
                      parent.appendChild(div);
                    }
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <UserIcon size={16} />
              )}
            </div>
            <div>
              <div className="name">{displayName}</div>
              <div className="role">{item.user?.role?.toUpperCase()}</div>
            </div>
          </UserInfo>
        );
      },
    },
    {
      title: "Amount",
      key: "amount",
      render: (item: any) => (
        <AmountText>{Helper.formatCurrency(item.amount, settings)}</AmountText>
      ),
    },
    {
      title: "Method",
      key: "method",
      render: (item: any) => (
        <MethodBadge method={item.method}>
          {item.method.toUpperCase()}
        </MethodBadge>
      ),
    },
    {
      title: "Proof",
      key: "proof",
      render: (item: any) =>
        item.proof ? (
          <ProofThumb
            src={`${bucketUrl}/${item.proof}`}
            onClick={() =>
              showModal(
                <ImageViewer
                  src={`${bucketUrl}/${item.proof}`}
                  onClose={hideModal}
                />,
              )
            }
          />
        ) : (
          <span style={{ color: "#9ca3af", fontSize: "12px" }}>N/A</span>
        ),
    },
    {
      title: "Status",
      key: "status",
      render: (item: any) => (
        <StatusPill status={item.status}>
          {item.status === "pending" && <Clock size={12} />}
          {item.status === "approved" && <CheckCircle2 size={12} />}
          {item.status === "rejected" && <XCircle size={12} />}
          <span>
            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
          </span>
        </StatusPill>
      ),
    },
    {
      title: "Created At",
      key: "createdAt",
      render: (item: any) => new Date(item.createdAt).toLocaleString(),
    },
    {
      title: "Action",
      key: "action",
      render: (item: any) => (
        <div style={{ display: "flex", gap: "8px" }}>
          {item.status === "pending" && (
            <>
              <Button
                title="Approve"
                onClick={() => handleApprove(item)}
                bg="success100"
              />
              <Button
                title="Reject"
                onClick={() => handleReject(item)}
                bg="red500"
              />
            </>
          )}
          <Button
            title=""
            icon={<Eye size={16} />}
            onClick={() =>
              showModal(
                <ViewDetailsModal
                  item={item}
                  settings={settings}
                  bucketUrl={bucketUrl}
                  onClose={hideModal}
                />,
              )
            }
            bg="grey500"
          />
        </div>
      ),
    },
  ];

  return (
    <PageWrapper>
      <HeaderArea>
        <Title textone="Finance" texttwo="Wallet Load Requests" />
        <p style={{ marginTop: "10px", color: "#6b7280", fontSize: "14px" }}>
          Review and verify wallet loading requests from users across all
          modules.
        </p>
      </HeaderArea>

      <FilterRow>
        <FilterItem>
          <Label>Search</Label>
          <Input
            type="text"
            placeholder="Search by name, email, phone..."
            value={params.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
          />
        </FilterItem>
        <FilterItem>
          <Label>Time Frame</Label>
          <Select
            value={params.dateRange}
            onChange={(e) => handleFilterChange("dateRange", e.target.value)}
          >
            <option value="">All Time</option>
            <option value="today">Today</option>
            <option value="this_week">This Week</option>
            <option value="this_month">This Month</option>
            <option value="custom">Custom Time</option>
          </Select>
        </FilterItem>
        {params.dateRange === "custom" && (
          <>
            <FilterItem>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={params.startDate}
                onChange={(e) =>
                  handleFilterChange("startDate", e.target.value)
                }
              />
            </FilterItem>
            <FilterItem>
              <Label>End Date</Label>
              <Input
                type="date"
                value={params.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
              />
            </FilterItem>
          </>
        )}
      </FilterRow>

      <TabsRow>
        {["pending", "approved", "rejected"].map((s) => (
          <Tab
            key={s}
            active={params.status === s}
            onClick={() => setParams({ ...params, status: s, page: 1 })}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </Tab>
        ))}
      </TabsRow>

      <TableCard>
        <Table
          columns={columns}
          data={data}
          loading={loading}
          totalItems={totalItems}
          totalPages={totalPages}
          itemsPerPage={params.limit}
          pageControlled={params.page}
          onPageChange={(page) => setParams({ ...params, page })}
        />
      </TableCard>
    </PageWrapper>
  );
};

// --- Sub-components ---

const ActionModal = ({ type, item, onClose, onSuccess }: any) => {
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (type === "reject" && !note.trim()) {
      toast.error("Please provide a reason for rejection.");
      return;
    }
    setLoading(true);
    try {
      if (type === "approve") {
        await WalletLoad.approve({ id: item.id, data: { adminNote: note } });
      } else {
        await WalletLoad.reject({ id: item.id, data: { adminNote: note } });
      }
      toast.success(`Request ${type}d successfully`);
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || `Failed to ${type} request`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalContent>
      <ModalTitle>
        {type === "approve" ? "Confirm Approval" : "Reject Request"}
      </ModalTitle>
      <ModalSubtitle>
        {type === "approve"
          ? `Are you sure you want to approve this load request of ${item.amount}? This will credit the user's wallet.`
          : `Please provide a reason for rejecting this load request.`}
      </ModalSubtitle>

      <div style={{ marginTop: "20px" }}>
        <label
          style={{
            fontSize: "14px",
            fontWeight: 500,
            display: "block",
            marginBottom: "8px",
          }}
        >
          Admin Note {type === "reject" && "*"}
        </label>
        <textarea
          rows={3}
          style={{
            width: "100%",
            padding: "10px",
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
            outline: "none",
          }}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={
            type === "approve" ? "Optional note..." : "Reason for rejection..."
          }
        />
      </div>

      <div
        style={{
          marginTop: "24px",
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
        }}
      >
        <Button title="Cancel" bg="grey500" onClick={onClose} />
        <Button
          title={
            loading
              ? "Processing..."
              : type === "approve"
                ? "Confirm Approve"
                : "Confirm Reject"
          }
          bg={type === "approve" ? "success100" : "red500"}
          onClick={handleSubmit}
          disabled={loading}
        />
      </div>
    </ModalContent>
  );
};

const ViewDetailsModal = ({ item, settings, bucketUrl, onClose }: any) => {
  return (
    <ModalContent>
      <ModalTitle>Request Details</ModalTitle>
      <div
        style={{
          marginTop: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <DetailRow>
          <span className="label">User</span>
          <span className="value">
            {item.user?.role === "store" && item.store?.storeName
              ? item.store.storeName
              : item.user?.name}{" "}
            ({item.user?.role?.toUpperCase()})
          </span>
        </DetailRow>
        <DetailRow>
          <span className="label">Amount</span>
          <span className="value" style={{ color: "#10b981", fontWeight: 700 }}>
            {Helper.formatCurrency(item.amount, settings)}
          </span>
        </DetailRow>
        <DetailRow>
          <span className="label">Method</span>
          <span className="value">{item.method.toUpperCase()}</span>
        </DetailRow>
        <DetailRow>
          <span className="label">Status</span>
          <span className="value" style={{ textTransform: "capitalize" }}>
            {item.status}
          </span>
        </DetailRow>
        {item.refId && (
          <DetailRow>
            <span className="label">Reference ID</span>
            <span className="value">{item.refId}</span>
          </DetailRow>
        )}
        {item.adminNote && (
          <DetailRow>
            <span className="label">Admin Note</span>
            <span className="value">{item.adminNote}</span>
          </DetailRow>
        )}
        {item.proof && (
          <div style={{ marginTop: "10px" }}>
            <span
              style={{
                fontSize: "13px",
                color: "#6b7280",
                display: "block",
                marginBottom: "8px",
              }}
            >
              Payment Proof
            </span>
            <img
              src={`${bucketUrl}/${item.proof}`}
              alt="Proof"
              style={{
                width: "100%",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
              }}
            />
          </div>
        )}
      </div>
      <div
        style={{
          marginTop: "24px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button title="Close" bg="grey500" onClick={onClose} />
      </div>
    </ModalContent>
  );
};

const ImageViewer = ({ src, onClose }: any) => (
  <div style={{ position: "relative" }}>
    <img
      src={src}
      alt="Proof Large"
      style={{ maxWidth: "100%", maxHeight: "80vh", borderRadius: "8px" }}
    />
    <div style={{ position: "absolute", top: "-40px", right: "0" }}>
      <Button title="Close" onClick={onClose} bg="grey500" />
    </div>
  </div>
);

// --- Styled Components ---

const PageWrapper = styled.div`
  padding: 0px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  align-items: flex-end;
  background: white;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
`;

const Select = styled.select`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  font-size: 14px;
  outline: none;
  min-width: 150px;
  &:focus {
    border-color: #3b82f6;
  }
`;

const Input = styled.input`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  font-size: 14px;
  outline: none;
  min-width: 250px;
  &:focus {
    border-color: #3b82f6;
  }
`;

const HeaderArea = styled.div`
  margin-bottom: 24px;
`;

const TabsRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const Tab = styled.div<{ active?: boolean }>`
  padding: 8px 16px 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.active ? "#007bff" : "#6b7280")};
  border-bottom: 2px solid
    ${(props) => (props.active ? "#007bff" : "transparent")};
  transition: all 0.2s;
  &:hover {
    color: #007bff;
  }
`;

const TableCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  .icon {
    width: 32px;
    height: 32px;
    background: #eff6ff;
    color: #3b82f6;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .name {
    font-weight: 600;
    color: #111827;
    font-size: 14px;
  }
  .role {
    font-size: 11px;
    color: #6b7280;
  }
`;

const AmountText = styled.span`
  font-weight: 700;
  color: #111827;
`;

const MethodBadge = styled.span<{ method: string }>`
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  background: ${(props) =>
    props.method === "esewa"
      ? "#ecfdf5"
      : props.method === "khalti"
        ? "#f5f3ff"
        : "#fefce8"};
  color: ${(props) =>
    props.method === "esewa"
      ? "#059669"
      : props.method === "khalti"
        ? "#7c3aed"
        : "#ca8a04"};
`;

const ProofThumb = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
  cursor: pointer;
  border: 1px solid #e5e7eb;
  &:hover {
    opacity: 0.8;
  }
`;

const StatusPill = styled.div<{ status: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 99px;
  font-size: 12px;
  font-weight: 600;
  width: fit-content;
  background: ${(props) =>
    props.status === "approved"
      ? "#ecfdf5"
      : props.status === "rejected"
        ? "#fef2f2"
        : "#fff7ed"};
  color: ${(props) =>
    props.status === "approved"
      ? "#059669"
      : props.status === "rejected"
        ? "#dc2626"
        : "#d97706"};
`;

const ModalContent = styled.div`
  width: 500px;
  max-width: 90vw;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const ModalSubtitle = styled.p`
  color: #6b7280;
  font-size: 14px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  .label {
    color: #6b7280;
    font-size: 13px;
  }
  .value {
    font-weight: 600;
    font-size: 14px;
  }
`;

export default WalletLoadRequests;
